import React from 'react';
import { isUndefined } from 'lodash-es';

import {
  ConfiguratorContextTrafficDetails,
  ConfiguratorContextTrafficPackage,
  ConfiguratorTrafficDetails,
  ConfiguratorTrafficDetailsClickHandler,
  ConfiguratorTrafficLayout,
  ConfiguratorTrafficPackage,
  ConfiguratorTrafficPackageClickHandler,
  ConfiguratorTrafficPackageToggleClickHandler,
  Maybe,
} from '../types';
import { getTrafficPackage, getTrafficPackageDetails } from '../utils/deprecated';

export function useTrafficStep(
  configTrafficDetails: ConfiguratorTrafficDetails,
  configTrafficPackages: ConfiguratorTrafficPackage[],
) {
  const [details, setDetails] = React.useState<ConfiguratorTrafficDetails>(configTrafficDetails);
  const [pack, setPack] = React.useState<Maybe<ConfiguratorTrafficPackage>>();
  const [packages, setPackages] = React.useState<ConfiguratorTrafficPackage[]>(configTrafficPackages);
  const [selection, setSelection] = React.useState<Maybe<string>>();

  const [layout, setLayout] = React.useState<ConfiguratorTrafficLayout>('default');
  const [isPackageOpen, setIsPackageOpen] = React.useState<boolean>(false);

  React.useEffect(() => {
    setDetails(configTrafficDetails);
    setPackages(configTrafficPackages);
  }, [configTrafficDetails, configTrafficPackages]);

  const handleDetailsClick = React.useCallback<ConfiguratorTrafficDetailsClickHandler>(() => {
    setLayout('details');
  }, []);

  const handlePremiumClick = React.useCallback<ConfiguratorTrafficDetailsClickHandler>(() => {
    setLayout('premium');
  }, []);

  const handlePackagesClick = React.useCallback<ConfiguratorTrafficDetailsClickHandler>(() => {
    setLayout('packages');
  }, []);

  const handleBackClick = React.useCallback<ConfiguratorTrafficDetailsClickHandler>(() => {
    setLayout('default');
  }, []);

  const handleTrafficPackageClick = React.useCallback<ConfiguratorTrafficPackageClickHandler>(
    (nextPackageId) => {
      const nextSelection = nextPackageId === selection ? undefined : nextPackageId;

      setSelection(nextSelection);
    },
    [selection],
  );

  const handleTrafficPackageToggleClick = React.useCallback<ConfiguratorTrafficPackageToggleClickHandler>(
    (currentPackageId, open) => {
      const pack = open ? getTrafficPackage(packages, currentPackageId) : undefined;

      setPack(pack);
      setIsPackageOpen(open);
    },
    [packages],
  );

  const trafficDetails = React.useMemo<ConfiguratorContextTrafficDetails>(() => {
    return {
      ...details,
      toDetailsClick: handleDetailsClick,
      toPremiumClick: handlePremiumClick,
      toPackagesClick: handlePackagesClick,
      onBackClick: handleBackClick,
    };
  }, [details, handleDetailsClick, handlePremiumClick, handlePackagesClick, handleBackClick]);

  const trafficPackage = React.useMemo<Maybe<ConfiguratorContextTrafficPackage>>(() => {
    if (isUndefined(pack)) {
      return;
    }

    return {
      ...pack,
      active: true,
      isOpen: true,
      onBackClick: () => handleTrafficPackageToggleClick(pack.id, false),
      onInfoClick: () => handleTrafficPackageToggleClick(pack.id, true),
      onClick: () => handleTrafficPackageClick(pack.id),
      details: getTrafficPackageDetails(pack),
    };
  }, [pack, handleTrafficPackageToggleClick, handleTrafficPackageClick]);

  const trafficPackages = React.useMemo<ConfiguratorContextTrafficPackage[]>(() => {
    return packages.map((trafficPackage) => ({
      ...trafficPackage,
      active: trafficPackage.id === selection,
      isOpen: isPackageOpen && trafficPackage.id === pack?.id,
      onBackClick: () => handleTrafficPackageToggleClick(trafficPackage.id, false),
      onInfoClick: () => handleTrafficPackageToggleClick(trafficPackage.id, true),
      onClick: () => handleTrafficPackageClick(trafficPackage.id),
      details: getTrafficPackageDetails(trafficPackage),
    }));
  }, [pack, packages, selection, isPackageOpen, handleTrafficPackageToggleClick, handleTrafficPackageClick]);

  return React.useMemo(
    () => ({
      trafficDetails,
      trafficPackage,
      trafficPackages,
      trafficLayout: layout,
    }),
    [trafficDetails, trafficPackage, trafficPackages, layout],
  );
}
